import { createActorContext } from "@xstate/react"
import { assign, setup, enqueueActions, raise } from "xstate"

import { navigateToPage } from "@/lib/flows/base/actions"
import type {
  BaseFlowContext,
  BaseFlowEvents,
  BaseFlowInput,
  BaseFlowMeta,
  FlowStep,
} from "@/lib/flows/base/types"

export const companyFlowSteps: FlowStep[] = [
  {
    id: "business-details",
    title: "Business Details",
    states: [
      { stateId: "BusinessDetails", substep: 1 },
      { stateId: "BusinessAddress", substep: 2 },
      { stateId: "BusinessAdditionalDetails", substep: 3 },
    ],
  },
  {
    id: "business-ownership",
    title: "Business Ownership",
    states: [
      { stateId: "BusinessOwnershipPrimary", substep: 1 },
      { stateId: "BusinessOwnershipSecondary", substep: 2 },
    ],
  },
  {
    id: "connect-bank",
    title: "Connect Bank",
    states: [{ stateId: "PlaidConnection", substep: 1 }],
  },
  {
    id: "debt-table",
    title: "Debt Table",
    states: [{ stateId: "DebtTable", substep: 1 }],
  },
  {
    id: "articles-of-incorporation",
    title: "Start Applying",
    states: [{ stateId: "ArticlesOfIncorporation", substep: 1 }],
  },
]

export const companyFlowMachine = setup({
  types: {
    input: {} as BaseFlowInput & {
      hasMultipleOwners: boolean
      initialHasMultipleOwners: boolean
      requiresExtraDetails: boolean
    },
    context: {} as BaseFlowContext & {
      hasMultipleOwners: boolean
      initialHasMultipleOwners: boolean
      requiresExtraDetails: boolean
    },
    events: {} as
      | BaseFlowEvents
      | {
          type: "setHasMultipleOwners"
          hasMultipleOwners: boolean
        }
      | {
          type: "setRequiresExtraDetails"
          requiresExtraDetails: boolean
        },
    meta: {} as BaseFlowMeta,
  },
  actions: {
    navigateToPage,
    processNext: enqueueActions(({ context, enqueue }) => {
      if (context.onNextOverride) {
        try {
          context.onNextOverride()
        } catch (error) {
          console.error("Error executing onNextOverride callback:", error)
        }
        enqueue.assign(() => ({
          onNextOverride: undefined,
          onBackOverride: undefined,
        }))
      } else {
        enqueue.raise({ type: "proceedNext" })
      }
    }),
    processBack: enqueueActions(({ context, enqueue }) => {
      if (context.onBackOverride) {
        try {
          context.onBackOverride()
        } catch (error) {
          console.error("Error executing onBackOverride callback:", error)
        }
        enqueue.assign(() => ({
          onNextOverride: undefined,
          onBackOverride: undefined,
        }))
      } else {
        enqueue.raise({ type: "proceedBack" })
      }
    }),
  },
  guards: {
    hasMultipleOwners: ({ context }) => context.hasMultipleOwners,
    requiresExtraDetails: ({ context }) => context.requiresExtraDetails,
    // True only when the user just opted into a new additional owner (vs.
    // editing a company that already has multiple owners).
    isAddingAdditionalOwner: ({ context }) =>
      context.hasMultipleOwners && !context.initialHasMultipleOwners,
  },
}).createMachine({
  initial: "BusinessDetails",

  // Assign the initial context as the `input` value provided to the machine
  context: ({ input }) => input,

  // Global event handlers
  on: {
    next: { actions: "processNext" },
    back: { actions: "processBack" },
    setNextOverride: {
      actions: assign(({ event }) => ({
        onNextOverride: event.onNextOverride,
      })),
    },
    setBackOverride: {
      actions: assign(({ event }) => ({
        onBackOverride: event.onBackOverride,
      })),
    },
    setHasMultipleOwners: {
      actions: assign(({ event }) => ({
        hasMultipleOwners: event.hasMultipleOwners,
      })),
    },
    setRequiresExtraDetails: {
      actions: assign({
        requiresExtraDetails: ({ event }) => event.requiresExtraDetails,
      }),
    },

    setFormErrors: {
      actions: assign({
        formErrors: ({ event: { formErrors } }) => formErrors,
      }),
    },
    clearFormErrors: {
      actions: assign({ formErrors: {} }),
    },

    reset: {
      target: ".BusinessDetails",
    },
  },

  // State definitions
  states: {
    BusinessDetails: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/details",
        },
      },
      on: {
        proceedNext: "BusinessAddress",
      },
    },
    BusinessAddress: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/address",
        },
      },
      on: {
        proceedNext: [
          {
            target: "BusinessAdditionalDetails",
            guard: "requiresExtraDetails",
          },
          {
            target: "BusinessOwnershipPrimary",
          },
        ],
        proceedBack: "BusinessDetails",
      },
    },
    BusinessAdditionalDetails: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/extra-details",
        },
      },
      on: {
        proceedNext: "BusinessOwnershipPrimary",
        proceedBack: "BusinessAddress",
      },
    },
    BusinessOwnershipPrimary: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/ownership-details",
          search: { step: "primary" },
        },
      },
      on: {
        next: [
          {
            // When adding an owner, advance to the secondary step before any
            // `returnTo` override fires. Plain edits fall through to `next`.
            // The `returnTo` override must stay in flow state across this hop
            // (it isn't kept in the URL), so the user lands back on the summary.
            guard: "isAddingAdditionalOwner",
            actions: raise({ type: "proceedNext" }),
          },
          { actions: "processNext" },
        ],
        proceedNext: [
          {
            // If there are multiple owners, go to the secondary ownership step
            target: "BusinessOwnershipSecondary",
            guard: "hasMultipleOwners",
          },
          {
            // If there is only one owner, go directly to Plaid connection
            target: "PlaidConnection",
          },
        ],
        proceedBack: [
          {
            target: "BusinessAdditionalDetails",
            guard: "requiresExtraDetails",
          },
          {
            target: "BusinessAddress",
          },
        ],
      },
    },
    BusinessOwnershipSecondary: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/ownership-details",
          search: { step: "additional" },
        },
      },
      on: {
        proceedNext: "PlaidConnection",
        proceedBack: "BusinessOwnershipPrimary",
      },
    },
    PlaidConnection: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/plaid",
        },
      },
      on: {
        proceedNext: "DebtTable",
        proceedBack: [
          {
            target: "BusinessOwnershipSecondary",
            guard: "hasMultipleOwners",
          },
          {
            target: "BusinessOwnershipPrimary",
          },
        ],
      },
    },
    DebtTable: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/debt-table",
        },
      },
      on: {
        proceedNext: "ArticlesOfIncorporation",
        proceedBack: "PlaidConnection",
      },
    },
    ArticlesOfIncorporation: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/articles-of-incorporation",
        },
      },
      on: {
        proceedNext: "CompanyFlowEnd",
        proceedBack: "DebtTable",
      },
    },
    CompanyFlowEnd: {
      type: "final",
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/resume",
        },
      },
    },
  },
})

export const CompanyFlowContext = createActorContext(companyFlowMachine)
